import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card, Alert } from 'react-bootstrap';
import { createSimpleNewsTickerInstance } from '../../../Models/SimpleNewsTicker';
import { toast, ToastContainer } from 'react-toastify';
import { SketchPicker } from 'react-color';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faGlobe } from '@fortawesome/free-solid-svg-icons';
import { LanguageService } from '../../../Services/Axios/Api';
import LanguageSelector from '../../../Components/LanguageSelector/LanguageSelector';

const NewsTickerBuilder = () => {
    const [ticker, setTicker] = useState(null);
    const [loading, setLoading] = useState(true);
    const [newItem, setNewItem] = useState('');
    const [activeColorPicker, setActiveColorPicker] = useState(null);
    const [tempColor, setTempColor] = useState(null);
    const [newsItems, setNewsItems] = useState([]);
    const [isEnabled, setIsEnabled] = useState(true);
    const [title, setTitle] = useState('');
    
    // Language states
    const [currentLanguage, setCurrentLanguage] = useState('');
    const [availableLanguages, setAvailableLanguages] = useState([]);   
    const [languageAlertType, setLanguageAlertType] = useState('info');
    const [showLanguageAlert, setShowLanguageAlert] = useState(false);
    const [isLanguagesLoaded, setIsLanguagesLoaded] = useState(false);

    useEffect(() => {
        loadLanguages();
    }, []);

    // Load data after languages are loaded and current language is set
    useEffect(() => {
        if (isLanguagesLoaded && currentLanguage) {
            loadTickerData();
        }
    }, [isLanguagesLoaded, currentLanguage]);

    const loadLanguages = async () => {
        try {
            const response = await LanguageService.getLanguages();
            setAvailableLanguages(response.result);
            
            // Set default language from API
            const defaultLanguage = response.result.find(lang => lang.isDefault)?.code || 'en';
            setCurrentLanguage(defaultLanguage);
            setIsLanguagesLoaded(true);
            
            console.log('NewsTickerBuilder: Language loaded:', defaultLanguage);
        } catch (error) {
            console.error('Error loading languages:', error);
            toast.error('Failed to load languages');
            setCurrentLanguage('en');
            setIsLanguagesLoaded(true);
        }
    };

    const loadTickerData = async () => {
        try {
            console.log('NewsTickerBuilder: Loading data for language:', currentLanguage);
            const tickerInstance = createSimpleNewsTickerInstance();
            await tickerInstance.load(currentLanguage);
            setTicker(tickerInstance);
            setNewsItems(tickerInstance.getNewsItems());
            setIsEnabled(tickerInstance.getIsEnabled());
            setTitle(tickerInstance.getTitle());
            setLoading(false);
        } catch (error) {
            console.error('Error loading news ticker data:', error);
            toast.error('Failed to load news ticker data');
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            ticker.setIsEnabled(isEnabled);
            ticker.setTitle(title);
            await ticker.save();
            toast.success('News ticker saved successfully');
        } catch (error) {
            toast.error('Failed to save news ticker');
        }
    };

    const handleAddItem = () => {
        if (newItem.trim()) {
            const updatedItems = [...newsItems, newItem.trim()];
            ticker.setNewsItems(updatedItems);
            setNewsItems(updatedItems);
            setNewItem('');
            toast.success('News item added');
        }
    };

    const handleRemoveItem = (index) => {
        const updatedItems = newsItems.filter((_, i) => i !== index);
        ticker.setNewsItems(updatedItems);
        setNewsItems(updatedItems);
        toast.success('News item removed');
    };

    const handleUpdateItem = (index, value) => {
        const updatedItems = newsItems.map((item, i) => 
            i === index ? value : item
        );
        ticker.setNewsItems(updatedItems);
        setNewsItems(updatedItems);
    };

    const handleLanguageChange = async (newLanguage) => {
        setCurrentLanguage(newLanguage);
        setShowLanguageAlert(true);
        setLanguageAlertType('info');
        toast.success(`Switched to ${availableLanguages.find(l => l.code === newLanguage)?.name || newLanguage.toUpperCase()}`);
        setTimeout(() => setShowLanguageAlert(false), 3000);
        // loadTickerData will be called automatically by the useEffect when currentLanguage changes
    };

    const renderColorPicker = (show, color, onChange, onClose) => {
        if (!show) return null;
        return (
            <div style={{ position: 'absolute', zIndex: 2 }}>
                <div 
                    style={{ position: 'fixed', top: '0', right: '0', bottom: '0', left: '0' }}
                    onClick={onClose}
                />
                <SketchPicker 
                    color={tempColor || color}
                    onChange={(color) => {
                        setTempColor(color.hex);
                        onChange(color);
                    }}
                    onChangeComplete={(color) => {
                        onChange(color);
                        setTempColor(null);
                    }}
                />
            </div>
        );
    };

    const renderColorSwatch = (color, onClick) => (
        <div 
            style={{
                width: '30px',
                height: '30px',
                backgroundColor: tempColor || color,
                border: '1px solid #ccc',
                marginRight: '10px',
                cursor: 'pointer'
            }}
            onClick={onClick}
        />
    );

    if (loading) return <div>Loading...</div>;
    if (!ticker) return <div>Error loading news ticker</div>;

    return (
        <Container fluid>
            <ToastContainer />
            <Row className="mb-4">
                <Col>
                    <h2>News Ticker Builder</h2>
                </Col>
            </Row>

            {/* Language Management Card */}
            <Card className="mb-4 language-management-card">
                <Card.Header className="language-management-header">
                    <Row className="">
                        <Col md={6}>
                            <div className="d-flex align-items-center gap-3">
                                <FontAwesomeIcon icon={faGlobe} className="text-primary" />
                                <div>
                                    <h6 className="mb-0">Language Management</h6>
                                    <small className="text-muted">
                                        Managing content for: <strong>{availableLanguages.find(l => l.code === currentLanguage)?.name}</strong>
                                    </small>
                                </div>
                            </div>
                        </Col>
                        <Col md={6} className="d-flex justify-content-end">
                            <LanguageSelector
                                currentLanguage={currentLanguage}
                                availableLanguages={availableLanguages}
                                onLanguageChange={handleLanguageChange}
                                showBadge={false}
                                showCopyFromLanguage={false}
                            />
                        </Col>
                    </Row>
                </Card.Header>
                
                {/* Language Alert */}
                {showLanguageAlert && (
                    <Card.Body className="p-3 language-alert-container">
                        <Alert
                            variant={languageAlertType} 
                            dismissible 
                            onClose={() => setShowLanguageAlert(false)}
                            className="mb-0"
                        >
                            <div className="d-flex align-items-center gap-2">
                                <FontAwesomeIcon icon={faGlobe} />
                                <div>
                                    {languageAlertType === 'warning' ? (
                                        <>
                                            <strong>Translation Required:</strong> You are now editing the {availableLanguages.find(l => l.code === currentLanguage)?.name} version. 
                                            {currentLanguage !== 'en' && ' Consider copying content from English to start with.'}
                                        </>
                                    ) : (
                                        <>
                                            <strong>Language Switched:</strong> You are now editing the {availableLanguages.find(l => l.code === currentLanguage)?.name} version of this page.
                                        </>
                                    )}
                                </div>
                            </div>
                        </Alert>
                    </Card.Body>
                )}
            </Card>

            <Row>
                <Col md={6}>
                    <Card className="mb-4">
                        <Card.Header>General Settings</Card.Header>
                        <Card.Body>
                            <Form.Group className="mb-3">
                                <Form.Label>Enable News Ticker</Form.Label>
                                <Form.Check
                                    type="switch"
                                    id="enable-ticker"
                                    checked={isEnabled}
                                    onChange={(e) => setIsEnabled(e.target.checked)}
                                    label={isEnabled ? "Enabled" : "Disabled"}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Title</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder="Enter news ticker title"
                                />
                            </Form.Group>
                        </Card.Body>
                    </Card>

                    <Card className="mb-4">
                        <Card.Header>News Items</Card.Header>
                        <Card.Body>
                            <Form.Group className="mb-3">
                                <Form.Label>Add New Item</Form.Label>
                                <div className="d-flex">
                                    <Form.Control
                                        type="text"
                                        value={newItem}
                                        onChange={(e) => setNewItem(e.target.value)}
                                        placeholder="Enter news item"
                                    />
                                    <Button
                                        variant="primary"
                                        onClick={handleAddItem}
                                        className="ms-2"
                                    >
                                        Add
                                    </Button>
                                </div>
                            </Form.Group>

                            <div className="news-items-list">
                                {newsItems.map((item, index) => (
                                    <div key={index} className="d-flex align-items-center mb-2">
                                        <Form.Control
                                            type="text"
                                            value={item}
                                            onChange={(e) => handleUpdateItem(index, e.target.value)}
                                            className="me-2"
                                        />
                                        <Button
                                            variant="danger"
                                            size="sm"
                                            onClick={() => handleRemoveItem(index)}
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={6}>
                    <Card className="mb-4">
                        <Card.Header>Style Settings</Card.Header>
                        <Card.Body>
                            <Form.Group className="mb-3">
                                <Form.Label>Container Background Color</Form.Label>
                                <div className="d-flex align-items-center">
                                    {renderColorSwatch(
                                        ticker.getBackgroundColor(),
                                        () => setActiveColorPicker('container')
                                    )}
                                    <span>{tempColor && activeColorPicker === 'container' ? tempColor : ticker.getBackgroundColor()}</span>
                                    {renderColorPicker(
                                        activeColorPicker === 'container',
                                        ticker.getBackgroundColor(),
                                        (color) => ticker.setBackgroundColor(color.hex),
                                        () => setActiveColorPicker(null)
                                    )}
                                </div>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Text Color</Form.Label>
                                <div className="d-flex align-items-center">
                                    {renderColorSwatch(
                                        ticker.getTextColor(),
                                        () => setActiveColorPicker('text')
                                    )}
                                    <span>{tempColor && activeColorPicker === 'text' ? tempColor : ticker.getTextColor()}</span>
                                    {renderColorPicker(
                                        activeColorPicker === 'text',
                                        ticker.getTextColor(),
                                        (color) => ticker.setTextColor(color.hex),
                                        () => setActiveColorPicker(null)
                                    )}
                                </div>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Title Background Color</Form.Label>
                                <div className="d-flex align-items-center">
                                    {renderColorSwatch(
                                        ticker.getTitleBackgroundColor(),
                                        () => setActiveColorPicker('titleBg')
                                    )}
                                    <span>{tempColor && activeColorPicker === 'titleBg' ? tempColor : ticker.getTitleBackgroundColor()}</span>
                                    {renderColorPicker(
                                        activeColorPicker === 'titleBg',
                                        ticker.getTitleBackgroundColor(),
                                        (color) => ticker.setTitleBackgroundColor(color.hex),
                                        () => setActiveColorPicker(null)
                                    )}
                                </div>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Title Text Color</Form.Label>
                                <div className="d-flex align-items-center">
                                    {renderColorSwatch(
                                        ticker.getTitleColor(),
                                        () => setActiveColorPicker('titleText')
                                    )}
                                    <span>{tempColor && activeColorPicker === 'titleText' ? tempColor : ticker.getTitleColor()}</span>
                                    {renderColorPicker(
                                        activeColorPicker === 'titleText',
                                        ticker.getTitleColor(),
                                        (color) => ticker.setTitleColor(color.hex),
                                        () => setActiveColorPicker(null)
                                    )}
                                </div>
                            </Form.Group>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col className='d-flex justify-content-end'>
                    <Button variant="primary" onClick={handleSave}>
                        Save Changes
                    </Button>
                </Col>
            </Row>
        </Container>
    );
};

export default NewsTickerBuilder;